import React from "react";
import { Box, Typography, Container } from "@mui/material";
import { useParams } from "react-router-dom";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { caseStudies } from "../../data/CaseStudy";
import CaseStudyImage from "../../assets/images/CaseStudyImage.webp";
import CaseStudyFooter from "../../assets/images/CaseStudyFooter.jpg";
import { caseStudiesData } from "../../data/CaseStudyCard";
import { Helmet } from "react-helmet";

const CaseStudyDetailContent = () => {
  const { id } = useParams();
  const caseStudy = caseStudies.find((study) => study.id === id);

  if (!caseStudy) {
    return <Box>Case Study not found</Box>;
  }

  return (
    <>
      <Helmet>
        <title>Inteliphyle | {caseStudy.title}</title>
        <meta
          name="description"
          content={caseStudy.content?.[0]?.text || "Case study details"}
        />
      </Helmet>
      <Box sx={{ backgroundColor: "#ecebeb" }}>
        {/* Top Image/Video Section */}
        <Box
          sx={{
            width: "100%",
            backgroundColor: "#ecebeb",
            pt: { xs: "8rem", sm: "8rem" },
            pb: { xs: "2rem", sm: "1rem" },
            px: {
              xs: caseStudy.format[0].type === "video" ? "0" : "0rem",
              md: "2rem",
            },
          }}
        >
          <Container
            maxWidth="lg"
            sx={{
              px: {
                xs: caseStudy.format[0].type === "video" ? "1rem" : "1rem",
              },
            }}
          >
            {caseStudy.format[0].type === "video" ? (
              <Box
                sx={{
                  position: "relative",
                  paddingBottom: { xs: "56.25%", sm: "42.25%" },
                  height: 0,
                  overflow: "hidden",
                  borderRadius: "1rem",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  maxWidth: "800px",
                  margin: { xs: "1rem auto 0", sm: "2rem auto 0" },
                  backgroundColor: "#fff",
                  border: "1px solid rgba(0,0,0,0.1)",
                }}
              >
                <iframe
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                  src={caseStudy.format[0].url.replace("watch?v=", "embed/")}
                  title="Case Study Video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </Box>
            ) : (
              <Box
                sx={{
                  maxWidth: "1000px",
                  margin: "0 auto",
                  borderRadius: "1rem",
                  overflow: "hidden",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  position: "relative",
                  height: { xs: "175px", sm: "270px" },
                }}
              >
                <img
                  src={caseStudiesData[id - 1].image}
                  alt="Case Study"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                    objectPosition:
                      caseStudy.id === "1" ? "center" : "bottom center",
                  }}
                />
              </Box>
            )}
          </Container>
        </Box>

        {/* Content Section */}
        <Box
          sx={{
            margin: { xs: "0rem auto 0 auto", sm: "0 auto 0 auto" },
            maxWidth: "1000px",
            padding: { xs: "0 1rem", sm: "0 2rem" },
          }}
        >
          {caseStudy.format[0].type === "markdown" && (
            <Box>
              <Typography
                sx={{
                  fontFamily: "Montserrat",
                  fontSize: { xs: "1.4rem", sm: "1.6rem" },
                  fontWeight: "500",
                  marginTop: "0",
                }}
              >
                <span style={{ color: "rgba(255, 102, 0, 1)" }}>
                  Case study{" "}
                </span>
              </Typography>

              <Typography
                variant="h4"
                sx={{
                  fontFamily: "Plus Jakarta Sans",
                  fontWeight: "700",
                  fontSize: { xs: "1.4rem", sm: "1.8rem" },
                  marginTop: "1rem",
                  color: "#333",
                }}
              >
                {caseStudy.title}
              </Typography>

              {/* Dynamically render content based on type */}
              {caseStudy.content.map((item, index) => {
                if (item.type === "paragraph") {
                  return (
                    <Typography
                      key={index}
                      sx={{
                        fontFamily: "Plus Jakarta Sans",
                        marginTop: "1rem",
                        color: "rgba(51, 51, 51, 1)",
                      }}
                    >
                      {item.text}
                    </Typography>
                  );
                } else if (item.type === "image") {
                  return (
                    <Box
                      key={index}
                      sx={{
                        width: "100%",
                        marginTop: "2rem",
                        marginBottom: "2rem",
                      }}
                    >
                      <LazyLoadImage
                        src={item.url}
                        effect="blur"
                        width="100%"
                        height="100%"
                        alt={`Case study image ${index + 1}`}
                      />
                    </Box>
                  );
                }
                return null;
              })}
            </Box>
          )}

          {/* {caseStudy.id === "1" && (
          <Box
            sx={{
              mt: { xs: 2, sm: 3 },
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Montserrat",
                fontSize: { xs: "1.2rem", sm: "1.4rem" },
                fontWeight: "600",
                marginBottom: { xs: 0, sm: 0 },
                color: "rgba(255, 102, 0, 1)",
                lineHeight: 1.4,
                paddingBottom: "1rem",
              }}
            >
              MRSI Golden Key Awards 2024 Finalist
            </Typography>
          </Box>
        )} */}

          {/* Footer Section */}

          <Box
            sx={{
              mt: { xs: 2, sm: 2 },
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Montserrat",
                fontSize: { xs: "1rem", sm: "1.2rem" },
                fontWeight: "500",
                marginBottom: { xs: 0, sm: 0 },
                color: "#333",
                lineHeight: 1.4,
                padding: "1rem 0",
              }}
            >
              {caseStudy.footer}
            </Typography>

            <Box
              sx={{
                maxWidth: "800px",
                margin: "0 auto",
                transition: "transform 0.3s ease-in-out",
                "&:hover": {
                  transform: "scale(1.02)",
                },
              }}
            >
              {/* <LazyLoadImage
                            src={CaseStudyFooter}
                            alt='footer logo'
                            effect="blur"
                            width="30%"
                            style={{
                                borderRadius: '0.5rem',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                            }}
                        /> */}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default CaseStudyDetailContent;
